import { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { motion } from "motion/react"
import { AfishaBanner } from "../../types/entities/banner"
import { getBannerLink } from "../../lib/afishaBanners"
import BannerAdDisclosure, { hasBannerAdInfo } from "./BannerAdDisclosure"

const AUTOPLAY_MS = 10_000
const GAP_PX = 16
const SWIPE_THRESHOLD_PX = 40
const MOBILE_EDGE_PX = 16
const MOBILE_PEEK_PX = 32
const CONTENT_X_PADDING_PX = 32 // px-4 слева и справа, как у карточек афиши
const DESKTOP_MAX_SLIDE_WIDTH = 1200 // 400px высоты при 3:1

/** Ширина контентной колонки — как у main + px-4 на /afisha */
function getContentGridWidth(viewportWidth: number): number {
    let mainMax = 768 // max-w-3xl
    if (viewportWidth >= 1280) mainMax = 1280 // xl:max-w-7xl
    else if (viewportWidth >= 1024) mainMax = 1024 // lg:max-w-5xl

    return Math.max(Math.min(viewportWidth, mainMax) - CONTENT_X_PADDING_PX, 0)
}

type Props = {
    banners: AfishaBanner[]
}

function mod(n: number, m: number) {
    return ((n % m) + m) % m
}

function BannerImage({ banner, alt }: { banner: AfishaBanner; alt: string }) {
    return (
        <img
            src={banner.image}
            alt={alt}
            className="absolute inset-0 size-full object-cover object-center"
            draggable={false}
        />
    )
}

function BannerFrame({
    banner,
    dimmed = false,
}: {
    banner: AfishaBanner
    dimmed?: boolean
}) {
    const alt = banner.title ?? "Промо-баннер"

    return (
        <div className="relative aspect-[3/1] w-full overflow-hidden rounded-[20px] border border-white/10 bg-white/[0.04] shadow-[0_8px_32px_rgba(0,0,0,0.22)] md:rounded-[24px]">
            <BannerImage banner={banner} alt={alt} />
            <div
                className="pointer-events-none absolute inset-0 bg-[#0a1028]/45 transition-opacity duration-500 ease-out"
                style={{ opacity: dimmed ? 1 : 0 }}
                aria-hidden
            />
            <div
                className="pointer-events-none absolute inset-0 bg-black/40 transition-opacity duration-500 ease-out"
                style={{ opacity: dimmed ? 1 : 0 }}
                aria-hidden
            />
            <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a1028]/40 via-transparent to-transparent transition-opacity duration-500 ease-out"
                style={{ opacity: dimmed ? 0 : 1 }}
                aria-hidden
            />
        </div>
    )
}

/** Стабильная обёртка: картинка не внутри Link/button, чтобы <img> не пересоздавались при смене активного слайда */
function BannerSlide({
    banner,
    isActive,
    linkTo,
    onGoTo,
    onActiveClick,
}: {
    banner: AfishaBanner
    isActive: boolean
    linkTo: string | null
    onGoTo: () => void
    onActiveClick: (e: React.MouseEvent) => void
}) {
    const activeLabel = banner.title
        ? `Открыть «${banner.title}»`
        : linkTo
          ? "Перейти"
          : "Промо-баннер"

    return (
        <div className={`relative h-full w-full ${isActive ? "group" : ""}`}>
            <BannerFrame banner={banner} dimmed={!isActive} />
            {isActive && (
                <div
                    className="pointer-events-none absolute inset-0 overflow-hidden rounded-[20px] opacity-0 transition-opacity duration-500 group-hover:opacity-100 md:rounded-[24px]"
                    style={{
                        background: "linear-gradient(135deg, rgba(52,155,230,0.1) 0%, transparent 55%)",
                    }}
                    aria-hidden
                />
            )}
            {isActive ? (
                linkTo ? (
                    <Link
                        to={linkTo}
                        state={{ pageTitle: banner.title }}
                        onClick={onActiveClick}
                        aria-label={activeLabel}
                        className="absolute inset-0 z-10 block rounded-[20px] md:rounded-[24px]"
                    />
                ) : (
                    <div
                        aria-label={activeLabel}
                        className="absolute inset-0 z-10 block rounded-[20px] md:rounded-[24px]"
                    />
                )
            ) : (
                <button
                    type="button"
                    onClick={onGoTo}
                    aria-label={banner.title ? `Показать: ${banner.title}` : "Показать слайд"}
                    className="absolute inset-0 z-10 cursor-pointer rounded-[20px] md:rounded-[24px]"
                />
            )}
        </div>
    )
}

function NavArrow({
    direction,
    onClick,
    inset,
}: {
    direction: "prev" | "next"
    onClick: () => void
    inset: number
}) {
    return (
        <button
            type="button"
            onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                onClick()
            }}
            aria-label={direction === "prev" ? "Предыдущий слайд" : "Следующий слайд"}
            style={direction === "prev" ? { left: inset } : { right: inset }}
            className="absolute top-1/2 z-30 hidden size-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/[0.92] text-[#16284a] shadow-[0_4px_20px_rgba(0,0,0,0.35)] transition-transform hover:scale-105 active:scale-95 md:flex md:size-10">
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden className={direction === "next" ? "rotate-180" : ""}>
                <path
                    d="M6.5 1L1.5 7l5 6"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    )
}

function AutoplayProgress({
    index,
    paused,
    progressEpoch,
    left,
    width,
    count,
    below = false,
}: {
    index: number
    paused: boolean
    progressEpoch: number
    left: number
    width: number
    count: number
    below?: boolean
}) {
    const progressKey = `${index}-${progressEpoch}`
    if (below) {
        return (
            <div className="md:hidden flex justify-center gap-1.5 pt-3 pb-3" aria-hidden>
                {Array.from({ length: count }, (_, i) => {
                    if (i === index) {
                        return (
                            <div
                                key={i}
                                className="relative h-[3px] w-5 shrink-0 overflow-hidden rounded-full bg-white/12">
                                <div
                                    key={progressKey}
                                    className="afisha-banner-progress h-full rounded-full bg-[#349be6] glow-primary"
                                    style={{
                                        animationDuration: `${AUTOPLAY_MS}ms`,
                                        animationPlayState: paused ? "paused" : "running",
                                    }}
                                />
                            </div>
                        )
                    }

                    return (
                        <div
                            key={i}
                            className={`h-[3px] w-2 shrink-0 rounded-full ${i < index ? "bg-white/40" : "bg-white/20"}`}
                        />
                    )
                })}
            </div>
        )
    }

    return (
        <div
            className="absolute bottom-0 z-20 hidden px-4 pb-3 md:block md:px-6 md:pb-4"
            style={{ left, width }}
            aria-hidden>
            <div className="h-[3px] overflow-hidden rounded-full bg-white/12">
                <div
                    key={progressKey}
                    className="afisha-banner-progress h-full rounded-full bg-[#349be6] glow-primary"
                    style={{
                        animationDuration: `${AUTOPLAY_MS}ms`,
                        animationPlayState: paused ? "paused" : "running",
                    }}
                />
            </div>
        </div>
    )
}

export default function AfishaBannerSlider({ banners }: Props) {
    const containerRef = useRef<HTMLDivElement>(null)
    const touchStartRef = useRef<{ x: number; y: number } | null>(null)
    const didSwipeRef = useRef(false)
    const autoplayTimerRef = useRef<ReturnType<typeof setTimeout>>()
    const autoplayDeadlineRef = useRef(0)
    const remainingMsRef = useRef(AUTOPLAY_MS)
    const [containerWidth, setContainerWidth] = useState(0)
    const [index, setIndex] = useState(0)
    const [paused, setPaused] = useState(false)
    const [progressEpoch, setProgressEpoch] = useState(0)
    const [hasAnimated, setHasAnimated] = useState(false)
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
    )

    const count = banners.length
    const multi = count > 1
    const mobileCarousel = isMobile && multi
    let slideWidth =
        containerWidth > 0
            ? mobileCarousel
                ? containerWidth - MOBILE_EDGE_PX - GAP_PX - MOBILE_PEEK_PX
                : isMobile
                  ? containerWidth
                  : getContentGridWidth(containerWidth)
            : 0

    if (!isMobile && slideWidth > DESKTOP_MAX_SLIDE_WIDTH) {
        slideWidth = DESKTOP_MAX_SLIDE_WIDTH
    }

    const trackOffset = mobileCarousel
        ? MOBILE_PEEK_PX - index * (slideWidth + GAP_PX)
        : containerWidth / 2 - slideWidth / 2 - index * (slideWidth + GAP_PX)
    const bannerHeight = slideWidth > 0 ? slideWidth / 3 : undefined
    const arrowInset = containerWidth > 0 ? (containerWidth - slideWidth) / 2 - 8 : 0

    const activeIndex = count > 0 ? mod(index, count) : 0
    // Виртуальные слоты вокруг текущего индекса — баннеры повторяются по кругу,
    // поэтому лента бесконечная в обе стороны
    const slots = multi ? Array.from({ length: 5 }, (_, k) => index - 2 + k) : [0]

    const goTo = useCallback((next: number) => {
        setHasAnimated(true)
        setIndex(next)
    }, [])

    const goNext = useCallback(() => goTo(index + 1), [goTo, index])
    const goPrev = useCallback(() => goTo(index - 1), [goTo, index])

    const clearAutoplayTimer = useCallback(() => {
        if (autoplayTimerRef.current != null) {
            window.clearTimeout(autoplayTimerRef.current)
            autoplayTimerRef.current = undefined
        }
    }, [])

    const pauseAutoplay = useCallback(() => {
        clearAutoplayTimer()
        remainingMsRef.current = Math.max(0, autoplayDeadlineRef.current - Date.now())
        setPaused(true)
    }, [clearAutoplayTimer])

    const resumeAutoplay = useCallback(() => {
        setPaused(false)
    }, [])

    const startAutoplayTimer = useCallback(
        (delayMs: number) => {
            clearAutoplayTimer()
            if (count <= 1) return

            const delay = Math.max(delayMs, 0)
            remainingMsRef.current = delay
            autoplayDeadlineRef.current = Date.now() + delay

            autoplayTimerRef.current = window.setTimeout(() => {
                remainingMsRef.current = AUTOPLAY_MS
                goNext()
            }, delay)
        },
        [clearAutoplayTimer, count, goNext],
    )

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0]
        if (!touch) return

        touchStartRef.current = { x: touch.clientX, y: touch.clientY }
        didSwipeRef.current = false
    }, [])

    const handleTouchEnd = useCallback(
        (e: React.TouchEvent) => {
            const start = touchStartRef.current
            touchStartRef.current = null
            if (!start || !multi) return

            const touch = e.changedTouches[0]
            if (!touch) return

            const dx = touch.clientX - start.x
            const dy = touch.clientY - start.y

            if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) return

            didSwipeRef.current = true
            if (dx < 0) goNext()
            else goPrev()
        },
        [goNext, goPrev, multi],
    )

    const handleActiveBannerClick = useCallback((e: React.MouseEvent) => {
        if (!didSwipeRef.current) return

        e.preventDefault()
        didSwipeRef.current = false
    }, [])

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)")
        const onChange = () => setIsMobile(mq.matches)

        onChange()
        mq.addEventListener("change", onChange)
        return () => mq.removeEventListener("change", onChange)
    }, [])

    useEffect(() => {
        setIndex(0)
    }, [count])

    useEffect(() => {
        const node = containerRef.current
        if (!node) return

        const measure = () => setContainerWidth(node.offsetWidth)
        measure()

        const observer = new ResizeObserver(measure)
        observer.observe(node)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        remainingMsRef.current = AUTOPLAY_MS
        setProgressEpoch(epoch => epoch + 1)
    }, [index])

    useEffect(() => {
        if (count <= 1 || paused) return

        startAutoplayTimer(remainingMsRef.current)
        return clearAutoplayTimer
    }, [count, paused, index, startAutoplayTimer, clearAutoplayTimer])

    if (count === 0) return null

    const progressLeft = mobileCarousel ? MOBILE_EDGE_PX : (containerWidth - slideWidth) / 2

    return (
        <div
            className="relative w-full pb-3 md:left-1/2 md:w-screen md:max-w-[100vw] md:-translate-x-1/2"
            onMouseEnter={pauseAutoplay}
            onMouseLeave={resumeAutoplay}
            onFocus={pauseAutoplay}
            onBlur={resumeAutoplay}>
            <div
                ref={containerRef}
                className="relative w-full touch-pan-y overflow-hidden"
                style={bannerHeight ? { height: bannerHeight } : { aspectRatio: "3 / 1" }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}>
                    {slideWidth > 0 && (
                        <motion.div
                            className="absolute inset-0 min-h-0"
                            initial={false}
                            animate={{ x: trackOffset }}
                            transition={
                                hasAnimated
                                    ? { duration: 0.55, ease: [0.32, 0.72, 0, 1] }
                                    : { duration: 0 }
                            }>
                            {slots.map(slot => {
                                const banner = banners[mod(slot, count)]
                                const isActive = slot === index
                                const linkTo = getBannerLink(banner)

                                return (
                                    <div
                                        key={slot}
                                        className="absolute top-0 h-full min-h-0"
                                        style={{
                                            width: slideWidth,
                                            left: slot * (slideWidth + GAP_PX),
                                        }}>
                                        <BannerSlide
                                            banner={banner}
                                            isActive={isActive}
                                            linkTo={linkTo}
                                            onGoTo={() => goTo(slot)}
                                            onActiveClick={handleActiveBannerClick}
                                        />
                                        {hasBannerAdInfo(banner) && (
                                            <div className="pointer-events-none absolute right-1 top-1 z-20 md:right-4 md:top-4">
                                                <div className="pointer-events-auto">
                                                    <BannerAdDisclosure banner={banner} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </motion.div>
                    )}

                    {multi && slideWidth > 0 && (
                        <>
                            <NavArrow direction="prev" onClick={goPrev} inset={Math.max(arrowInset, 4)} />
                            <NavArrow direction="next" onClick={goNext} inset={Math.max(arrowInset, 4)} />
                            {!mobileCarousel && (
                                <AutoplayProgress
                                    index={activeIndex}
                                    paused={paused}
                                    progressEpoch={progressEpoch}
                                    left={progressLeft}
                                    width={slideWidth}
                                    count={count}
                                />
                            )}
                        </>
                    )}
            </div>

            {multi && slideWidth > 0 && mobileCarousel && (
                <AutoplayProgress
                    index={activeIndex}
                    paused={paused}
                    progressEpoch={progressEpoch}
                    left={progressLeft}
                    width={slideWidth}
                    count={count}
                    below
                />
            )}
        </div>
    )
}
